import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tooltipable from "~/components/effects/tooltipable";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { apiFetch } from "~/lib/fetchClient";

const ALL_OPTION = { value: "all", label: "All" };

const SelectPromptCategory = ({
  onCategorySelected,
  selectedCategory = null,
  isDisabled = false,
  moduleTag = null,
  channels = null,
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (moduleTag) {
      params.module = moduleTag;
    }
    if (channels) {
      params.channel = channels;
    }
    const req = apiFetch.get("/ai_prompt_categories", { params });

    (async () => {
      setIsLoading(true);
      try {
        const body = await req;
        if (!cancelled) {
          setOptions(Array.isArray(body) ? body : []);
          setHasError(false);
        }
      } catch (err) {
        if (!cancelled && err?.name !== "AbortError") {
          setHasError(true);
          // PRD AC 3.3.2 hides the dropdown silently on fetch error; the
          // event gives us observability into how often that fallback is
          // hit without surfacing anything to the user.
          captureEvent("ai_assist_library_categories_fetch_failed", {
            module: moduleTag,
            channel: channels,
            status: err?.status ?? null,
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      req.abort();
    };
  }, [moduleTag, channels]);

  // "All" is always first; category list from API is already alphabetized.
  const mergedOptions = useMemo(() => [ALL_OPTION, ...options], [options]);

  const handleChange = useCallback(
    (option) => {
      if (!option || option.value === ALL_OPTION.value) {
        onCategorySelected(null);
        return;
      }
      onCategorySelected(option);
    },
    [onCategorySelected],
  );

  // PRD AC 3.3.2: on categories fetch error, hide the dropdown silently and let prompts load unfiltered.
  if (hasError) {
    return null;
  }

  // Pass the full option object (not a scalar) so the Select wrapper renders
  // the label consistently, especially for the synthetic "All" sentinel.
  const selectedOption = selectedCategory ?? ALL_OPTION;

  return (
    <div className="d-flex flex-column">
      <div className="d-flex align-items-center mb-1">
        <label htmlFor="prompt-category" className="mb-0 mr-1">
          Category
        </label>
        <Tooltipable
          trigger="click"
          placement="top"
          text="Filter templates by recruiting use case."
          className="d-inline-flex"
        >
          <FontAwesomeIcon
            icon={["fal", "fa-info-circle"]}
            className="text-secondary"
            style={{ cursor: "pointer", fontSize: "0.85em" }}
            data-testid="prompt-category-info-icon"
          />
        </Tooltipable>
      </div>
      <Select
        id="prompt-category"
        name="prompt-category"
        isClearable={false}
        isSearchable
        isLoading={isLoading}
        isDisabled={isDisabled || isLoading}
        value={selectedOption}
        options={mergedOptions}
        onChange={handleChange}
      />
    </div>
  );
};

SelectPromptCategory.propTypes = {
  onCategorySelected: PropTypes.func.isRequired,
  selectedCategory: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  }),
  isDisabled: PropTypes.bool,
  moduleTag: PropTypes.string,
  channels: PropTypes.arrayOf(PropTypes.string),
};

export default SelectPromptCategory;
